import { Box } from "@mui/material";

/**
 * A user's avatar: their real profile photo when one is on file (currently only
 * Spotify supplies one - Apple/MusicKit exposes no API for it), falling back to a
 * colored initials circle otherwise. Used for both the header's account button and
 * each song row's "added by" cell, so the same person always renders identically in
 * both places - they're driven by the exact same imageUrl value from the backend,
 * not two independent lookups that could drift.
 */

const AVATAR_COLORS = [
    "#1D9BF0", "#7856FF", "#00BA7C", "#F91880", "#FF7A00", "#E0245E", "#17BF63", "#794BC4",
];

function initialsOf(name: string): string {
    const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) {
        return "?";
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

function colorFor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) | 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

interface UserAvatarProps {
    username?: string | null;
    imageUrl?: string | null;
    size?: number;
}

export default function UserAvatar({ username, imageUrl, size = 24 }: UserAvatarProps) {
    const name = username?.trim() || "Unknown";

    if (imageUrl) {
        return (
            <Box
                component="img"
                src={imageUrl}
                alt={name}
                title={name}
                // A broken/expired photo URL falls back to initials rather than a
                // broken-image icon - swap the element itself via a data attribute
                // read by the sibling span is overkill here, so just hide on error
                // and let the initials span underneath (rendered together) show.
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                sx={{
                    width: size,
                    height: size,
                    flexShrink: 0,
                    borderRadius: "50%",
                    objectFit: "cover",
                }}
            />
        );
    }

    return (
        <Box
            component="span"
            title={name}
            aria-label={name}
            sx={{
                width: size,
                height: size,
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: colorFor(name),
                color: "#04121C",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: Math.max(9, Math.round(size * 0.42)),
                fontWeight: 700,
                lineHeight: 1,
                userSelect: "none",
            }}
        >
            {initialsOf(name)}
        </Box>
    );
}
